import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type LayoutData = {
  rows: number;
  cols: number;
  seats: boolean[][];
};

interface BookingLayoutDesignerProps {
  show: boolean;
  initialLayout?: LayoutData | null;
  initialName?: string;
  initialSeatNumberingStart?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  onClose: () => void;
  onSave: (name: string, layout: LayoutData, seatNumberingStart: string) => void;
  onSeatNumberingStartChange?: (value: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right') => void;
}

export default function BookingLayoutDesigner({ show, initialLayout = null, initialName = '', initialSeatNumberingStart = 'top-left', onClose, onSave, onSeatNumberingStartChange }: BookingLayoutDesignerProps) {
  const [name, setName] = useState(initialName || 'Layout');
  const [rows, setRows] = useState(initialLayout?.rows || 5);
  const [cols, setCols] = useState(initialLayout?.cols || 10);
  const [seats, setSeats] = useState<boolean[][]>(initialLayout?.seats || Array.from({ length: rows }, () => Array.from({ length: cols }, () => true)));
  const [rowsField, setRowsField] = useState(String(rows));
  const [colsField, setColsField] = useState(String(cols));
  const [seatNumberingStart, setSeatNumberingStart] = useState<string>(initialSeatNumberingStart);

  const rowLabelFromIndex = (idx: number): string => {
    let effectiveIdx = idx;
    if (seatNumberingStart === 'bottom-left' || seatNumberingStart === 'bottom-right') {
        effectiveIdx = rows - 1 - idx;
    }
    let n = effectiveIdx;
    let s = '';
    while (n >= 0) {
      s = String.fromCharCode(65 + (n % 26)) + s;
      n = Math.floor(n / 26) - 1;
    }
    return s;
  };

  useEffect(() => {
    setSeats(Array.from({ length: rows }, (_, r) => Array.from({ length: cols }, (_, c) => seats[r]?.[c] ?? true)));
  }, [rows, cols]);

  const toggleSeat = (r: number, c: number) => {
    setSeats(prev => {
      const copy = prev.map(row => row.slice());
      copy[r][c] = !copy[r][c];
      return copy;
    });
  };

  const save = () => {
    onSave(name, { rows, cols, seats }, seatNumberingStart);
    onClose();
  };

  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg shadow-2xl w-full max-w-[95vw] lg:max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-white text-2xl font-bold">Design Seat Layout</h2>
          <button onClick={onClose} className="text-gray-300 hover:text-white">✕</button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Layout Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white" />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm text-gray-300 mb-1">Rows</label>
                <input type="number" min={1} max={80} value={rowsField} onChange={(e) => { const v = e.target.value; const digits = v.replace(/\D/g, ''); setRowsField(digits); if (digits !== '') setRows(parseInt(digits)); }} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white" />
              </div>
              <div className="flex-1">
                <label className="block text-sm text-gray-300 mb-1">Columns</label>
                <input type="number" min={1} max={40} value={colsField} onChange={(e) => { const v = e.target.value; const digits = v.replace(/\D/g, ''); setColsField(digits); if (digits !== '') setCols(parseInt(digits)); }} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white" />
              </div>
            </div>
             <div>
               <label className="block text-sm text-gray-300 mb-1">
                 Seat Numbering Start Position
               </label>
               <select
                 value={seatNumberingStart}
                 onChange={(e) => { const v = e.target.value as 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'; setSeatNumberingStart(v); if (onSeatNumberingStartChange) onSeatNumberingStartChange(v); }}
                 className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-indigo-500"
               >
                 <option value="top-left">Top Left (Default)</option>
                 <option value="top-right">Top Right</option>
                 <option value="bottom-left">Bottom Left</option>
                 <option value="bottom-right">Bottom Right</option>
               </select>
             </div>
          </div>

          <div className="lg:col-span-2">
            <div className="mb-4 p-4 bg-gray-700 rounded text-center">
              <p className="text-sm text-gray-300 mb-2">SCREEN</p>
              <div className="h-1 bg-gray-500 rounded"></div>
            </div>
            <div className="flex justify-start overflow-x-auto overflow-y-auto max-h-[60vh] p-2 scrollbar-hide">
              <div className="inline-flex flex-col gap-3 min-w-max w-fit">
                {(() => { const sizeClass = cols >= 30 ? 'w-6 h-6' : cols >= 24 ? 'w-7 h-7' : 'w-8 h-8'; const headerWidthClass = sizeClass.split(' ')[0]; return (
                <div className="flex items-center gap-4">
                  <div className={headerWidthClass} />
                  <div className="flex gap-2">
                    {Array.from({ length: cols }).map((_, idx) => {
                      const isRightToLeft = seatNumberingStart === 'top-right' || seatNumberingStart === 'bottom-right';
                      return (
                        <div key={`col-${idx}`} className={`${headerWidthClass} text-center text-xs font-semibold text-white`}>
                          {isRightToLeft ? cols - idx : idx + 1}
                        </div>
                      );
                    })}
                  </div>
                </div>
                ); })()}
                {seats.map((row, rIdx) => (
                  <div key={rIdx} className="flex items-center gap-4">
                    {(() => { const sizeClass = cols >= 30 ? 'w-6 h-6' : cols >= 24 ? 'w-7 h-7' : 'w-8 h-8'; const headerWidthClass = sizeClass.split(' ')[0]; return (
                    <div className={`${headerWidthClass} text-center text-sm font-semibold text-white`}>{rowLabelFromIndex(rIdx)}</div>
                    ); })()}
                    <div className="flex gap-2">
                      {row.map((hasSeat, cIdx) => {
                        const sizeClass = cols >= 30 ? 'w-6 h-6' : cols >= 24 ? 'w-7 h-7' : 'w-8 h-8';
                        let displaySeatNumber = 0;
                        
                        const isRightToLeft = seatNumberingStart === 'top-right' || seatNumberingStart === 'bottom-right';

                        if (isRightToLeft) {
                             for (let i = cols - 1; i > cIdx; i--) {
                                if (row[i]) displaySeatNumber++
                            }
                        } else {
                             for (let i = 0; i < cIdx; i++) {
                                if (row[i]) displaySeatNumber++
                            }
                        }

                        const seatId = `${rowLabelFromIndex(rIdx)}${displaySeatNumber + 1}`;
                        return (
                          <button
                            key={`${rIdx}-${cIdx}`}
                            onClick={() => toggleSeat(rIdx, cIdx)}
                            className={`${sizeClass} rounded flex items-center justify-center text-[10px] font-bold ${hasSeat ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}
                            title={seatId}
                          >
                             {hasSeat && (displaySeatNumber + 1)}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <button onClick={save} className="w-full px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">Save Layout</button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
